import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { shortId } from '../lib/shortId';

/**
 * Mapa tenantId → nombre legible del tenant, tomado de las asignaciones del usuario: el `scopeRefName`
 * de los assignments con scope `TENANT` (que el backend puebla en /me cuando dispone del nombre).
 *
 * Limitación conocida (TD-WEBAPP-019): /me/profile NO devuelve `tenantDisplayName` a nivel raíz,
 * y el `scopeRefName` solo está poblado para algunos assignments. Cuando no hay nombre disponible se
 * cae a un id acortado y etiquetado por el caller — NUNCA al UUID crudo. El fix definitivo es que el
 * backend exponga el displayName del tenant en /me. Ver TECH_DEBT.md.
 */
export function useTenantNameById(): Map<string, string> {
    const { roleAssignments } = useAuth();
    return useMemo(() => {
        const map = new Map<string, string>();
        for (const a of roleAssignments) {
            if (a.scope === 'TENANT' && a.scopeRef && a.scopeRef !== '*' && a.scopeRefName) {
                map.set(a.scopeRef, a.scopeRefName);
            }
        }
        return map;
    }, [roleAssignments]);
}

/**
 * Nombre legible del tenant del usuario actual (`user.tenantId`). Resuelve vía `useTenantNameById`
 * (assignments TENANT con `scopeRefName`); si no hay nombre, cae a un id acortado — NUNCA al UUID crudo.
 * Devuelve `undefined` si todavía no hay usuario cargado.
 */
export function useTenantName(): string | undefined {
    const { user } = useAuth();
    const byId = useTenantNameById();
    if (!user) return undefined;
    return byId.get(user.tenantId) ?? shortId(user.tenantId);
}
